/**
 * Root App Component - Frontend Consolidation
 *
 * Provides unified routing for all four applications with role-based access control.
 * Integrates landing, admin, policy-navigator, and evidence-extractor into a single app.
 *
 * Requirements: 1.2, 1.3, 1.4, 1.5, 1.6
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider } from './shared/src/theme/ThemeProvider';
import { RoleGuard, AuthGuard } from './shared/src/auth';
import { Dashboard } from './landing/src/pages/Dashboard';
import { AdminPage } from './admin/src/AdminPage';
import { PolicyNavigatorPage } from './policy-navigator/src';
import { EvidenceExtractorPage } from './evidence-extractor/src';

// Auth0 configuration
const auth0Config = {
  domain: import.meta.env.VITE_AUTH0_DOMAIN || '',
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
  authorizationParams: {
    redirect_uri: window.location.origin,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE || '', // Include audience globally
    scope: 'openid profile email offline_access', // Include offline_access for refresh tokens
  },
  cacheLocation: 'localstorage' as const, // Cache tokens to avoid repeated consent
  useRefreshTokens: true, // Enable refresh tokens for better UX
};

/**
 * Root App component with unified routing
 *
 * Features:
 * - Single React Router setup for all applications (Req 1.6)
 * - Role-based route protection (Req 3.4, 3.5)
 * - Centralized Auth0 provider (Req 3.1)
 * - Route mapping for all four applications (Req 1.2, 1.3, 1.4, 1.5)
 */
export function App(): JSX.Element {
  return (
    <Auth0Provider {...auth0Config}>
      <ThemeProvider>
        <Router>
          <Routes>
            {/* Landing page - requires authentication but no specific roles (Req 1.2) */}
            <Route
              path="/"
              element={
                <AuthGuard>
                  <Dashboard />
                </AuthGuard>
              }
            />

            {/* Admin routes - requires admin role (Req 1.3) */}
            <Route
              path="/admin/*"
              element={
                <RoleGuard requiredRoles={['admin']}>
                  <AdminPage />
                </RoleGuard>
              }
            />

            {/* Policy Navigator routes - requires policy_navigator role (Req 1.4) */}
            <Route
              path="/policy-navigator/*"
              element={
                <RoleGuard requiredRoles={['policy_navigator']}>
                  <PolicyNavigatorPage />
                </RoleGuard>
              }
            />

            {/* Evidence Extractor routes - requires evidence_extractor role (Req 1.5) */}
            <Route
              path="/evidence-extractor/*"
              element={
                <RoleGuard requiredRoles={['evidence_extractor']}>
                  <EvidenceExtractorPage />
                </RoleGuard>
              }
            />

            {/* Catch-all route - redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </Auth0Provider>
  );
}
